import { createApi } from "@reduxjs/toolkit/query/react";
import type {
  CandidateMetricsResponse,
  CandidateResumeAnalysisApiRequest,
  CandidateResumeAnalysisApiResponse,
  CandidateSearchRequest,
  CandidateSearchResponse,
  CandidatesStatusResponse,
  CreateCandidateApiRequest,
  CreateCandidateApiResponse,
  UpdateCandidateApiRequest,
  CandidateFeedbackResponse,
  CandidatePollingApiRequest,
  CandidatePollingApiResponse,
} from "./types";
import {
  CANDIDATE,
  CANDIDATE_ROUTES,
  HTTP_METHODS,
} from "@cointervue/shared/constants";
import { createBaseQueryWithReauth } from "@cointervue/shared/api";

import { buildCandidateFormData } from "@cointervue/shared";

export const candidatesApi = createApi({
  reducerPath: "CandidatesApi",
  baseQuery: createBaseQueryWithReauth(CANDIDATE.href),
  tagTypes: ["Candidates"],
  endpoints: (builder) => ({
    candidatesStatus: builder.query<CandidatesStatusResponse, void>({
      query: () => ({
        url: CANDIDATE_ROUTES.candidateStatus,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      providesTags: [{ type: "Candidates", id: "STATUS" }],
    }),

    searchCandidates: builder.query<
      CandidateSearchResponse,
      CandidateSearchRequest
    >({
      query: ({ jobid, s, status, experience, token, sortBy }) => ({
        url: "/",
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        params: {
          ...(jobid && { jobid }),
          ...(s && { s }),
          ...(status && { status }),
          ...(experience && { experience: String(experience) }),
          ...(token && { token }),
          ...(sortBy && { sortBy }),
        },
      }),
      providesTags: (_, __, arg) => [
        { type: "Candidates", id: "LIST" },
        { type: "Candidates", id: arg.jobid ?? "ALL" },
      ],
    }),

    candidatesJobStatus: builder.query<CandidateMetricsResponse, string>({
      query: (jobId) => ({
        url: `${CANDIDATE_ROUTES.candidatesJobStatus}/${jobId}`,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      providesTags: (_, __, jobId) => [
        { type: "Candidates", id: "LIST" },
        { type: "Candidates", id: jobId },
      ],
    }),

    deleteCandidate: builder.mutation<
      string,
      {
        candidateId: string;
      }
    >({
      query: ({ candidateId }) => ({
        url: `/${candidateId}`,
        method: HTTP_METHODS.DELETE,
        headers: {
          accept: "application/json",
        },
      }),
      invalidatesTags: (_res, err) =>
        err
          ? []
          : [
              { type: "Candidates", id: "LIST" },
              { type: "Candidates", id: "STATUS" },
            ],
    }),

    candidateResumeAnalyze: builder.mutation<
      CandidateResumeAnalysisApiResponse,
      CandidateResumeAnalysisApiRequest
    >({
      query: ({ resume, jobId }) => {
        const formData = new FormData();
        formData.append("resume", resume);
        formData.append("jobId", jobId);
        return {
          url: CANDIDATE_ROUTES.resumeAnalyze,
          method: HTTP_METHODS.POST,
          headers: {
            accept: "application/json",
          },
          body: formData,
        };
      },
      invalidatesTags: (_res, err) =>
        err
          ? []
          : [
              { type: "Candidates", id: "LIST" },
              { type: "Candidates", id: "STATUS" },
            ],
    }),
    createCandidate: builder.mutation<
      CreateCandidateApiResponse,
      CreateCandidateApiRequest
    >({
      query: (request) => ({
        url: "",
        method: HTTP_METHODS.POST,
        headers: { accept: "application/json" },
        body: buildCandidateFormData(request),
      }),
      invalidatesTags: (_res, err) =>
        err
          ? []
          : [
              { type: "Candidates", id: "LIST" },
              { type: "Candidates", id: "STATUS" },
            ],
    }),

    updateCandidate: builder.mutation<
      CreateCandidateApiResponse,
      UpdateCandidateApiRequest
    >({
      query: ({ candidateId, ...rest }) => ({
        url: "",
        method: HTTP_METHODS.PUT,
        headers: { accept: "application/json" },
        params: { candidateId },
        body: buildCandidateFormData(rest),
      }),
      invalidatesTags: (_res, err) =>
        err
          ? []
          : [
              { type: "Candidates", id: "LIST" },
              { type: "Candidates", id: "STATUS" },
            ],
    }),
    downloadCandidateResume: builder.mutation<
      null,
      { candidateId: string; fileName?: string }
    >({
      async queryFn({ candidateId, fileName }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: CANDIDATE_ROUTES.downloadCandidateResume + candidateId,
          method: HTTP_METHODS.GET,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        const blob = result.data as Blob;

        let downloadName = fileName || "candidate-resume.pdf";

        // If the file is a DOCX word document, convert the file extension to .docx
        if (
          blob.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) {
          downloadName = downloadName.replace(/\.pdf$/i, ".docx");
        }

        // download immediately
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();

        a.remove();
        window.URL.revokeObjectURL(url);

        // return serializable value only
        return { data: null };
      },
    }),
    getCandidateFeedback: builder.query<
      CandidateFeedbackResponse,
      { candidateId: string }
    >({
      query: ({ candidateId }) => ({
        url: CANDIDATE_ROUTES.interviewFeedback,
        method: HTTP_METHODS.GET,
        params: { candidateId },
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
    }),

    candidatePolling: builder.query<
      CandidatePollingApiResponse,
      CandidatePollingApiRequest
    >({
      query: ({ candidateId }) => ({
        url: CANDIDATE_ROUTES.candidatePolling,
        method: HTTP_METHODS.GET,
        params: { candidateId },
      }),
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, { candidateId }) => [
        { type: "Candidates", id: `POLLING_${candidateId}` },
      ],
    }),

    downloadFeedbackReport: builder.mutation<
      null,
      { candidateId: string; fileName?: string }
    >({
      async queryFn({ candidateId, fileName }, _api, _extra, baseQuery) {
        const result = await baseQuery({
          url: CANDIDATE_ROUTES.DownloadFeedbackReport + candidateId,
          method: HTTP_METHODS.GET,
          responseHandler: (response) => response.blob(),
        });

        if (result.error) {
          return { error: result.error };
        }

        const rawBlob = result.data as Blob;
        const blob = new Blob([rawBlob], { type: "application/pdf" });

        let downloadName = fileName || "feedback-report.pdf";
        if (!downloadName.toLowerCase().endsWith(".pdf")) {
          downloadName = downloadName.replace(/\.[^/.]+$/, "") + ".pdf";
        }

        // download immediately
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = downloadName;
        document.body.appendChild(a);
        a.click();

        a.remove();
        window.URL.revokeObjectURL(url);

        // return serializable value only
        return { data: null };
      },
    }),
  }),
});

export const {
  useCandidatesStatusQuery,
  useSearchCandidatesQuery,
  useCandidatesJobStatusQuery,
  useDeleteCandidateMutation,
  useCandidateResumeAnalyzeMutation,
  useCreateCandidateMutation,
  useUpdateCandidateMutation,
  useDownloadCandidateResumeMutation,
  useGetCandidateFeedbackQuery,
  useLazyGetCandidateFeedbackQuery,
  useCandidatePollingQuery,
  useDownloadFeedbackReportMutation,
} = candidatesApi;

import type { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";

/**
 * A reusable RTK Query lifecycle helper (onQueryStarted) to handle cross-slice tag invalidation.
 * It waits for the mutation to succeed, then manually invalidates the "STATUS" tag
 * of the candidatesApi cache to force candidate dashboard metrics to refresh.
 */
export const invalidateCandidateStatusOnSuccess = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
    queryFulfilled: Promise<unknown>;
  },
) => {
  try {
    // Wait for the mutation query to resolve successfully
    await queryFulfilled;
    // Dispatch cache invalidation for candidate status metrics
    dispatch(
      candidatesApi.util.invalidateTags([
        { type: "Candidates", id: "STATUS" },
      ]),
    );
  } catch {
    // Fail silently: we only want to invalidate cache tags on successful requests.
    // Error handling is handled natively by the mutation hook in the components.
  }
};
